import { GenericAbortSignal } from 'axios';

import { LocationEpisodeLinkSet, LocationEpisodeLinkSetOptions } from 'models/flexible/LocationEpisodeLinkSet';
import { Paginated } from 'models/flexible/Paginated';
import type {
  AdminLinkSetIndexFields,
  AdminLinkSetIndexInclude,
  AdminLinkSetShowFields,
  AdminLinkSetShowInclude,
  AdminLinkSetUpdateParams,
  AdminLocationEpisodeLinkUpdateParams,
} from 'services/api/fields';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const adminLinkedRecordsQueryKeys = {
  index: ['adminLinkedRecords'] as const,
  show: (id: string) => ['adminLinkedRecords', id] as const,
};

const LINK_SETS_URL = 'admin/location_episode_link_sets';
const LINKS_URL = 'admin/location_episode_links';

export type AdminLinkedRecordIndexParams = BaseIndexQueryParams & {
  clientId?: string;
  archived?: string;
  isPendingReview?: string;
};

export async function adminIndexLinkedRecords<I extends AdminLinkSetIndexInclude = never>(
  params: AdminLinkedRecordIndexParams,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<Paginated<LocationEpisodeLinkSet<AdminLinkSetIndexFields<I>>>> {
  return http
    .get<
      Paginated<LocationEpisodeLinkSetOptions>
    >(LINK_SETS_URL, { params: { ...params, ...(includes?.length ? { include: includes.join(',') } : {}) }, signal })
    .then((res) => ({
      ...res.data,
      data: res.data.data.map((x) => new LocationEpisodeLinkSet(x)),
    })) as Promise<Paginated<LocationEpisodeLinkSet<AdminLinkSetIndexFields<I>>>>;
}

export async function adminShowLinkedRecord<I extends AdminLinkSetShowInclude = never>(
  id: string,
  params?: Record<string, any>,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<LocationEpisodeLinkSet<AdminLinkSetShowFields<I>>> {
  const includeParam = includes?.length ? { include: includes.join(',') } : {};
  return http
    .get<LocationEpisodeLinkSetOptions>(`${LINK_SETS_URL}/${id}`, { params: { ...params, ...includeParam }, signal })
    .then((res) => new LocationEpisodeLinkSet(res.data)) as Promise<LocationEpisodeLinkSet<AdminLinkSetShowFields<I>>>;
}

export async function updateLinkedRecord(id: string, params: AdminLinkSetUpdateParams) {
  return http.patch(`${LINK_SETS_URL}/${id}`, params);
}

export async function updateLocationEpisodeLink(id: string, params: AdminLocationEpisodeLinkUpdateParams) {
  return http.patch(`${LINKS_URL}/${id}`, params);
}
